import { useState } from "react";
import { Star, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function ReviewModal({ pending, onClose, onSubmitted }) {
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        subject_id: pending.other_id,
        application_id: pending.application_id,
        rating,
        comment: comment.trim() || null,
      });
      toast.success(`Review submitted for ${pending.other_name}`);
      onSubmitted?.();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="review-modal" className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-neutral-950 border border-neutral-800">
        <div className="flex items-center justify-between border-b border-neutral-800 px-6 py-4">
          <div>
            <div className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">Leave a review</div>
            <div className="text-sm text-neutral-100 mt-1">{pending.other_name} · <span className="text-neutral-500 uppercase text-[10px] tracking-widest">{pending.other_role}</span></div>
            <div className="text-xs text-neutral-500 mt-1 truncate">{pending.target_title}</div>
          </div>
          <button type="button" data-testid="review-modal-close" onClick={onClose} className="p-2 -mr-2 text-neutral-400 hover:text-amber-500" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2">Rating</div>
            <div className="flex gap-1" data-testid="review-stars">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  type="button"
                  key={s}
                  data-testid={`review-star-${s}`}
                  onMouseEnter={() => setHover(s)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => setRating(s)}
                  className="p-1"
                  aria-label={`${s} stars`}
                >
                  <Star className={`w-8 h-8 transition-colors ${(hover || rating) >= s ? "text-amber-500 fill-amber-500" : "text-neutral-700"}`} strokeWidth={2} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 block mb-2">Comment (optional)</label>
            <textarea
              data-testid="review-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              maxLength={500}
              placeholder="How was working with them?"
              className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none p-3 text-sm text-neutral-100"
            />
          </div>
        </div>
        <div className="border-t border-neutral-800 px-6 py-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-widest text-neutral-400 hover:text-amber-500">Cancel</button>
          <button data-testid="review-submit" disabled={submitting} className="px-6 py-2 bg-amber-500 text-neutral-950 uppercase tracking-widest text-xs font-bold hover:bg-amber-400 disabled:opacity-60 transition-colors">
            {submitting ? "Sending…" : "Submit"}
          </button>
        </div>
      </form>
    </div>
  );
}
